import React, { KeyboardEventHandler, MouseEventHandler, useEffect, useRef } from 'react';

import {
  FnOnDayClick,
  FnOnDayHover,
  NavigationValidationFunctions,
  useCalendarDispatch,
  useCalendarState,
  useHandleCalendarNavigation,
  useModifiers
} from './hooks';
import { Box, Inline } from '@front10/base-elements';
import { DayData, InternalEvent } from '../types';
// import { mergeDeep } from '../../../helpers/utils';
import {
  addMonthsNumber,
  getDayData,
  getFirstDayOfMonth,
  getModifiers,
  isDayInRange,
  lastDayOfMonth
} from './helper';
import { useWeekDaysLong, useDateFormat } from '../i18n';
import { useTranslation } from '@front10/utils';

export type DayItemProps = {
  dayData: DayData;
  onDayClick: FnOnDayClick;
  onDayHover: FnOnDayHover;
  setWasOpenPopup?: (value: boolean) => void;
  events?: InternalEvent[];
};

const DayItem = (props: DayItemProps) => {
  const { dayData, onDayHover, onDayClick, setWasOpenPopup, ...restProps } = props;

  //hooks
  const { state, format, modifiers, numberOfMonths } = useCalendarState();
  const dispatch = useCalendarDispatch();

  // validations functions
  const validations: NavigationValidationFunctions = {
    validateDay: (date: Date) => {
      const dateModifiers = getModifiers(date, modifiers);
      return (
        !dateModifiers.disabled &&
        dateModifiers.interactive &&
        !dateModifiers.blackout &&
        !dateModifiers.hidden
      );
    },
    dataFromDay: (date: Date) => {
      return getDayData(date, state.months);
    },
    isDateOutOfView: (date: Date) => {
      //show data when are off screen
      const endDisplayRange = lastDayOfMonth(
        addMonthsNumber(state.displayDate, numberOfMonths - 1)
      );
      const startDisplayRange = getFirstDayOfMonth(state.displayDate);
      return !isDayInRange(date, { start: startDisplayRange, end: endDisplayRange });
    }
  };

  const navigationHandler = useHandleCalendarNavigation({
    isPopopverOpen: state.popoverOpen,
    validations,
    initialDate: state?.preSelected,
    format,
    component: state.selectedComponent,
    onDayClick,
    dispatch,
    preSelected: state.preSelected,
    selectedRange: state.selectedRange,
    setWasOpenPopup: setWasOpenPopup,
    availableDays: state.availableDays
  });

  const ref = useRef<HTMLDivElement>(null);
  const translate = useTranslation();
  const daysLabels = useWeekDaysLong();
  const formatDate = useDateFormat();

  const {
    attributes: dayAttributes,
    style: modifiersStyles,
    modifier: dateModifiers
  } = useModifiers(dayData.date);
  const day = dayData.date;

  const isPreselectedDay = dateModifiers.preSelected;

  // const newPropsModifier = mergeDeep(restProps, modifiersStyles);

  useEffect(() => {
    if (isPreselectedDay && ref.current && state.focusSelectedDay) {
      ref.current.focus();
    }
  }, [isPreselectedDay, state.focusSelectedDay]);

  //events handlers
  const handleSelectDay: MouseEventHandler<HTMLDivElement> | undefined = !dateModifiers.interactive
    ? undefined
    : (event) => {
        event.preventDefault();
        if (day) {
          onDayClick(dayData);
        }
      };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> | undefined = !dateModifiers.interactive
    ? undefined
    : navigationHandler;

  const tabIndex = isPreselectedDay ? 0 : !dateModifiers.interactive ? undefined : -1;

  if (!day) {
    return (
      <Box
        // {...newPropsModifier}
        {...restProps}
        sx={modifiersStyles}
        className={`calendar-day fr-datepicker-empty`}
        as="td"
      >
        &nbsp;
      </Box>
    );
  }

  const getAriaLabel = () => {
    const formattedDate = formatDate(day, format);
    const dayName = day ? daysLabels[day?.getDay()] : '';
    const prefixArialLabel = dateModifiers.interactive
      ? translate('date-picker-date-available', `Choose`)
      : translate('date-picker-date-not-available', `Not available`);

    return `${prefixArialLabel} ${dayName} ${formattedDate}`;
  };

  return (
    <Box
      key={`calendar-day-${day?.getDate()} -${day?.getMonth()}`}
      className="calendar-day"
      as="td"
      p="1px"
      sx={modifiersStyles}
      onBlur={() => {
        dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: false });
      }}
      onFocus={() => {
        dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: true });
      }}
      onClick={handleSelectDay}
      onMouseEnter={(event) => {
        onDayHover(event, dayData);
      }}
      onKeyDown={handleKeyDown}
      aria-disabled={dateModifiers.disabled}
      {...dayAttributes}
    >
      <Box
        role="button"
        aria-label={getAriaLabel()}
        tabIndex={tabIndex}
        data-fr-day
        {...restProps}
        {...dayAttributes}
        ref={ref}
      >
        <Box className="calendar-day-inner">{day?.getDate()}</Box>
        {props.events ? (
          <Inline sx={{ '> *': { flex: 1 }, minHeight: '5px' }} gap="2px">
            {props.events.map((event, index) => (
              <Box key={index} sx={event.styles} />
            ))}
          </Inline>
        ) : null}
      </Box>
    </Box>
  );
};

export default DayItem;
